import { Inject, Injectable } from '@angular/core';
import { Router, ROUTER_CONFIGURATION } from '@angular/router';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { RegimeApiModel } from '../models/regime-api-model';
import { RegisterService } from '../services/register.service';
import {
  navigateToRouteWithReplacedParameters,
  TypedActivatedRouteSnapshot,
} from '../utils/route-utils';
import { RegisterData, RegisterParams } from './register.resolver';

export type RegimeParams = RegisterParams & {
  regimeId: string;
};

export type RegimeData = RegisterData & {
  regime: RegimeApiModel;
};

@Injectable({
  providedIn: 'root',
})
export class RegimeResolver {
  private static _regimes$: (Observable<RegimeApiModel[]> & { registerId?: number }) | undefined;

  public static clearCache() {
    RegimeResolver._regimes$ = undefined;
  }

  private getRegimes(registerId: number) {
    if (!RegimeResolver._regimes$ || RegimeResolver._regimes$.registerId !== registerId) {
      RegimeResolver._regimes$ = this.registerService.getRegimes(registerId).pipe(shareReplay());
    }

    RegimeResolver._regimes$.registerId = registerId;
    return RegimeResolver._regimes$;
  }

  // Prefer the same regime by name, else the same kind (Accounts or Tax), since names
  // differ between countries. Storage written before the kind was saved holds only
  // the old generic name, which maps to a kind.
  private static findStoredRegime(regimes: RegimeApiModel[], storage: Storage) {
    const regimeName = storage.getItem('regimeName');
    const byName = regimes.find(r => r.name === regimeName);
    if (byName) {
      return byName;
    }

    const legacyKinds: Record<string, string> = { Accounts: 'true', Tax: 'false' };
    const isAccounts =
      storage.getItem('regimeIsAccounts') ?? (regimeName ? legacyKinds[regimeName] : undefined);
    return isAccounts ? regimes.find(r => String(r.isAccounts) === isAccounts) : undefined;
  }

  constructor(
    private router: Router,
    private registerService: RegisterService,
    @Inject(ROUTER_CONFIGURATION) private routerConfig: { enableTracing: boolean },
  ) {}

  resolve(route: TypedActivatedRouteSnapshot<RegimeParams>): Observable<RegimeApiModel> {
    const registerId = +route.params.registerId;

    if (!registerId) {
      throw new Error('No registerId param');
    }

    const regimePath = route.params.regimeId || '';
    const regimeId = +regimePath.split('-')[0];

    return this.getRegimes(registerId).pipe(
      map(regimes => {
        let regime = regimes.find(r => r.id === regimeId);

        if (regime) {
          if (this.routerConfig.enableTracing) {
            console.log('Regime Resolver: resolved', regime);
          }
          for (const storage of [sessionStorage, localStorage]) {
            storage.setItem('regimeName', regime.name);
            storage.setItem('regimeIsAccounts', String(regime.isAccounts));
          }
        } else {
          // Try to get regime from session
          // Else try to get regime from local storage
          // Else fall back to default - includes when changing countries
          regime =
            RegimeResolver.findStoredRegime(regimes, sessionStorage) ||
            RegimeResolver.findStoredRegime(regimes, localStorage) ||
            regimes[0];

          if (!regime) {
            throw new Error('No regimes found');
          }

          if (this.routerConfig.enableTracing) {
            console.log('Regime Resolver: redirecting', regime);
          }
          navigateToRouteWithReplacedParameters(
            this.router,
            {
              regimeId: regime.id,
            },
            route,
          );
        }

        return regime;
      }),
    );
  }
}
